import { CRM_ADAPTERS } from "./emailThumbnail.adapters";
import { parseTemplate, renderTemplate } from "./emailThumbnail.template";
import {
  EmailThumbnailConfig,
  TemplateSegment,
  TextLayer,
} from "./emailThumbnail.types";
import {
  EmailThumbnailDiagnostic,
  validateEmailThumbnailConfig,
  validateGeneratedFragment,
} from "./emailThumbnail.validation";

export type GenerationContext = {
  schemaFields: string[];
};

export type GenerationResult = {
  html: string;
  diagnostics: EmailThumbnailDiagnostic[];
};

const escapeText = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeAttribute = escapeText;

const renderEscapedTemplate = (
  template: string,
  config: EmailThumbnailConfig
): string => {
  const adapter = CRM_ADAPTERS[config.crmId];
  return parseTemplate(template)
    .map((segment) =>
      segment.type === "text"
        ? escapeText(segment.value)
        : renderTemplate([segment], adapter, config.fieldMappings)
    )
    .join("");
};

const renderStyle = (
  layer: TextLayer,
  fontSize = layer.fontSize,
  lineHeight = layer.lineHeight
): string =>
  `font-family:${layer.fontFamily};font-weight:${layer.fontWeight};font-size:${fontSize}px;line-height:${lineHeight}px;color:${layer.color};`;

const renderStaticSpan = (
  layer: TextLayer,
  content: string,
  fontSize = layer.fontSize,
  lineHeight = layer.lineHeight
): string =>
  `<span style="${renderStyle(layer, fontSize, lineHeight)}">${content}</span>`;

const renderAmpScriptLiteral = (value: string): string[] => {
  const terms: string[] = [];
  let literal = "";
  const appendLiteral = (): void => {
    if (literal) {
      terms.push(`"${literal}"`);
      literal = "";
    }
  };

  Array.from(value).forEach((character) => {
    const characterCode =
      character === '"'
        ? 34
        : character === "%"
        ? 37
        : character === "<"
        ? 60
        : character === ">"
        ? 62
        : character === "&"
        ? 38
        : character === "\r"
        ? 13
        : character === "\n"
        ? 10
        : null;
    if (characterCode === null) {
      literal += character;
    } else {
      appendLiteral();
      terms.push(`Char(${characterCode})`);
    }
  });
  appendLiteral();
  return terms;
};

const renderNativeLengthExpression = (config: EmailThumbnailConfig): string => {
  const terms = parseTemplate(config.headline.template).flatMap(
    (segment: TemplateSegment) => {
      if (segment.type === "text") {
        return renderAmpScriptLiteral(segment.value);
      }
      const token = config.fieldMappings[segment.field];
      const attributeName = token.slice(2, -2);
      return [`AttributeValue("${attributeName}")`];
    }
  );
  return terms.length === 1 ? terms[0] : `Concat(${terms.join(", ")})`;
};

const renderNativeLengthSpans = (
  config: EmailThumbnailConfig,
  content: string
): string => {
  const [large, medium, small] = config.sizingBuckets;

  return [
    `%%[ SET @emailThumbnailHeadline = ${renderNativeLengthExpression(
      config
    )} ]%%`,
    `%%[ IF Length(@emailThumbnailHeadline) <= ${large.maxCharacters} THEN ]%%`,
    renderStaticSpan(
      config.headline,
      content,
      large.fontSize,
      large.lineHeight
    ),
    `%%[ ELSEIF Length(@emailThumbnailHeadline) <= ${medium.maxCharacters} THEN ]%%`,
    renderStaticSpan(
      config.headline,
      content,
      medium.fontSize,
      medium.lineHeight
    ),
    "%%[ ELSE ]%%",
    renderStaticSpan(
      config.headline,
      content,
      small.fontSize,
      small.lineHeight
    ),
    "%%[ ENDIF ]%%",
  ].join("");
};

const renderMailchimpBucketSpans = (
  config: EmailThumbnailConfig,
  content: string
): string => {
  const bucketToken = config.fieldMappings[config.bucketField as string];
  const bucketName = bucketToken.slice(2, -2);
  const [large, medium, small] = config.sizingBuckets;

  return [
    `*|IF:${bucketName}=large|*`,
    renderStaticSpan(
      config.headline,
      content,
      large.fontSize,
      large.lineHeight
    ),
    `*|ELSEIF:${bucketName}=medium|*`,
    renderStaticSpan(
      config.headline,
      content,
      medium.fontSize,
      medium.lineHeight
    ),
    "*|ELSE:|*",
    renderStaticSpan(
      config.headline,
      content,
      small.fontSize,
      small.lineHeight
    ),
    "*|END:IF|*",
  ].join("");
};

const renderHeadline = (config: EmailThumbnailConfig): string => {
  const content = renderEscapedTemplate(config.headline.template, config);
  if (config.sizingStrategy === "native-length") {
    return renderNativeLengthSpans(config, content);
  }
  if (config.sizingStrategy === "precomputed-bucket") {
    return renderMailchimpBucketSpans(config, content);
  }
  return renderStaticSpan(config.headline, content);
};

const renderContentTable = (config: EmailThumbnailConfig): string => {
  const cells: Record<string, string[]> = {};
  const appendLayer = (layer: TextLayer, html: string): void => {
    const key = `${layer.verticalSlot}:${layer.horizontalAlignment}`;
    cells[key] = [...(cells[key] || []), html];
  };
  appendLayer(config.headline, renderHeadline(config));
  if (config.supportingLine) {
    appendLayer(
      config.supportingLine,
      renderStaticSpan(
        config.supportingLine,
        renderEscapedTemplate(config.supportingLine.template, config)
      )
    );
  }
  if (config.playIndicator.enabled) {
    appendLayer(
      config.headline,
      `<span style="display:inline-block;width:${config.playIndicator.size}px;height:${config.playIndicator.size}px;line-height:${config.playIndicator.size}px;text-align:center;background-color:${config.playIndicator.backgroundColor};color:${config.playIndicator.foregroundColor};">&#9654;</span>`
    );
  }

  const rows = ["top", "center", "bottom"] as const;
  const columns = ["left", "center", "right"] as const;
  return `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" height="${
    config.height
  }" style="width:100%;height:${config.height}px;">${rows
    .map(
      (row) =>
        `<tr>${columns
          .map((column) => {
            const layer =
              config.headline.verticalSlot === row &&
              config.headline.horizontalAlignment === column
                ? config.headline
                : config.supportingLine?.verticalSlot === row &&
                  config.supportingLine.horizontalAlignment === column
                ? config.supportingLine
                : config.headline;
            const contents = cells[`${row}:${column}`] || [];
            return `<td align="${column}" valign="${
              row === "top" ? "top" : row === "bottom" ? "bottom" : "middle"
            }" style="padding:${contents.length ? layer.padding : 0}px;">${
              contents.join("<br />") || "&nbsp;"
            }</td>`;
          })
          .join("")}</tr>`
    )
    .join("")}</table>`;
};

const hasSizingSource = (config: EmailThumbnailConfig): boolean => {
  try {
    return parseTemplate(config.headline.template).some(
      (segment) => segment.type === "field"
    );
  } catch (_error) {
    return false;
  }
};

const renderHeadlineSample = (
  config: EmailThumbnailConfig,
  samples: Record<string, string>
): string => {
  try {
    return parseTemplate(config.headline.template)
      .map((segment) =>
        segment.type === "text"
          ? segment.value
          : samples[segment.field] ?? segment.field
      )
      .join("");
  } catch (_error) {
    return "";
  }
};

const validateContext = (
  config: EmailThumbnailConfig
): EmailThumbnailDiagnostic[] => {
  if (config.sizingStrategy === "native-length" && !hasSizingSource(config)) {
    return [
      {
        severity: "error",
        code: "unsupported-sizing-strategy",
        message: "Native length sizing requires a personalized headline field",
      },
    ];
  }
  return [];
};

export const generateEmailThumbnail = (
  config: EmailThumbnailConfig,
  context: GenerationContext
): GenerationResult => {
  const configDiagnostics = validateEmailThumbnailConfig(
    config,
    context.schemaFields
  );
  if (configDiagnostics.some((diagnostic) => diagnostic.severity === "error")) {
    return { html: "", diagnostics: configDiagnostics };
  }
  const diagnostics = validateContext(config);
  if (diagnostics.some((diagnostic) => diagnostic.severity === "error")) {
    return { html: "", diagnostics };
  }

  const backgroundUrl = escapeAttribute(config.backgroundUrl);
  const destinationUrl = renderEscapedTemplate(config.destinationUrl, config);
  const fallbackText = escapeAttribute(config.fallbackText);
  const html = [
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="${config.width}" style="width:${config.width}px;border-collapse:collapse;">`,
    '<tr><td align="center">',
    `<table role="presentation" border="0" cellpadding="0" cellspacing="0" width="${config.width}" height="${config.height}" style="width:${config.width}px;height:${config.height}px;">`,
    "<tr>",
    `<td background="${backgroundUrl}" width="${config.width}" height="${config.height}" style="background-color:${config.fallbackColor};background-image:url('${backgroundUrl}');background-position:center;background-repeat:no-repeat;background-size:cover;">`,
    `<!--[if gte mso 9]><v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:${config.width}px;height:${config.height}px;"><v:fill type="frame" src="${backgroundUrl}" color="${config.fallbackColor}" /><v:textbox inset="0,0,0,0"><![endif]-->`,
    `<a href="${destinationUrl}" title="${fallbackText}" aria-label="${fallbackText}" style="text-decoration:none;display:block;">`,
    renderContentTable(config),
    "</a>",
    "<!--[if gte mso 9]></v:textbox></v:rect><![endif]-->",
    "</td></tr></table>",
    "</td></tr></table>",
  ].join("");
  const fragmentDiagnostics = validateGeneratedFragment(html, {
    adapter: CRM_ADAPTERS[config.crmId],
    tokens: Object.values(config.fieldMappings),
  });
  return {
    html: fragmentDiagnostics.some(
      (diagnostic) => diagnostic.severity === "error"
    )
      ? ""
      : html,
    diagnostics: fragmentDiagnostics,
  };
};

export const renderSampleFragment = (
  html: string,
  samples: Record<string, string>,
  config: EmailThumbnailConfig
): string => {
  let previewHtml = html;
  if (config.sizingStrategy === "native-length") {
    const sampleLength = Array.from(
      renderHeadlineSample(config, samples)
    ).length;
    const branchIndex =
      sampleLength <= config.sizingBuckets[0].maxCharacters
        ? 0
        : sampleLength <= config.sizingBuckets[1].maxCharacters
        ? 1
        : 2;
    const sfmcBlock =
      /%%\[ SET @emailThumbnailHeadline = [\s\S]*? \]%%%%\[ IF Length\(@emailThumbnailHeadline\) <= \d+ THEN \]%%(<span\b[\s\S]*?<\/span>)%%\[ ELSEIF Length\(@emailThumbnailHeadline\) <= \d+ THEN \]%%(<span\b[\s\S]*?<\/span>)%%\[ ELSE \]%%(<span\b[\s\S]*?<\/span>)%%\[ ENDIF \]%%/g;
    previewHtml = previewHtml.replace(
      sfmcBlock,
      (_match, large, medium, small) => [large, medium, small][branchIndex]
    );
  }
  if (config.sizingStrategy === "precomputed-bucket") {
    const bucket = config.bucketField
      ? (samples[config.bucketField] || "small").toLowerCase()
      : "small";
    const branchIndex = bucket === "large" ? 0 : bucket === "medium" ? 1 : 2;
    const mailchimpBlock =
      /\*\|IF:[A-Z_][A-Z0-9_]*=large\|\*(<span\b[\s\S]*?<\/span>)\*\|ELSEIF:[A-Z_][A-Z0-9_]*=medium\|\*(<span\b[\s\S]*?<\/span>)\*\|ELSE:\|\*(<span\b[\s\S]*?<\/span>)\*\|END:IF\|\*/g;
    previewHtml = previewHtml.replace(
      mailchimpBlock,
      (_match, large, medium, small) => [large, medium, small][branchIndex]
    );
  }

  return Object.entries(config.fieldMappings).reduce(
    (rendered, [field, token]) => {
      const adapter = CRM_ADAPTERS[config.crmId];
      if (
        !adapter ||
        !adapter.isValidToken(token) ||
        !Object.prototype.hasOwnProperty.call(samples, field)
      ) {
        return rendered;
      }
      return rendered
        .split(token)
        .join(escapeText(samples[field]).replace(/\r?\n/g, "<br />"));
    },
    previewHtml
  );
};
